import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { SiteHeaderEn } from "@/components/SiteHeaderEn";
import { SiteFooterEn } from "@/components/SiteFooterEn";
import { LeadForm } from "@/components/LeadForm";
import { LeadSection } from "@/components/LeadSection";
import { cases } from "@/lib/cases-data";
import { enCases } from "@/lib/cases-en";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Freelancer: Stores, Development and CRO";
const DESCRIPTION =
  "Shopify freelancer for store builds, theme development and conversion work. €110 net per hour or fixed scope, working directly with Nicolai Schnepf.";

export const Route = createFileRoute("/en/")({
  component: EnHome,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/en` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en` },
      { rel: "alternate", hrefLang: "de", href: `${SITE}/` },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en` },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE}/` },
    ],
  }),
});

const principles = [
  {
    h: "You buy an outcome",
    p: "A defined scope and one price. What you pay for is a measurable change in revenue per session, not a stack of hours.",
  },
  {
    h: "The conversion rate decides",
    p: "More traffic multiplies whatever your store already does. If the store converts badly, paid traffic just makes the leak more expensive.",
  },
  {
    h: "Diagnosis before treatment",
    p: "Analytics, session recordings and checkout data first. Only then do we touch the store, so every change has a reason behind it.",
  },
  {
    h: "You hear a no upfront",
    p: "If the numbers say a sprint won't pay for itself, I tell you before we start rather than after the invoice.",
  },
];

function EnHome() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeaderEn />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                 Shopify Freelancer · Nicolai Schnepf
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] md:text-6xl">
                 Your Shopify store should sell, not just{" "}
                 <span className="text-signal">look good</span>.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                 I build new Shopify stores, improve existing themes and remove the points where
                 customers drop out. You work directly with me, not through an account manager.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 text-sm font-semibold">
                <Link
                  to="/en/pricing"
                  className="inline-flex items-center gap-1.5 text-primary"
                >
                  What it costs <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/en/cases" className="inline-flex items-center gap-1.5 text-primary">
                  Selected stores <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
              <LeadForm
                source="en-hero"
                lang="en"
                headline="Get a free assessment"
                messagePlaceholder="What should improve in your store?"
              />
          </div>
        </section>

        <section className="border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Does this sound familiar?</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {[
                ["The task list keeps growing", "Small changes pile up while technical work keeps you away from your business."],
                ["Visitors do not buy enough", "You pay for traffic, but product pages and checkout leave too many questions unanswered."],
                ["Waiting costs revenue", "Every month the same friction stays in place, more sales go to stores that are easier to use."],
              ].map(([title, text]) => (
                <div key={title} className="rounded-lg border border-border bg-card p-6">
                  <h3 className="font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">
              What you get, and how you measure it
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              The lever is revenue per visitor. Everything below serves that number.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {principles.map((p) => (
                <div key={p.h} className="rounded-lg border border-border bg-card p-6">
                  <h3 className="text-lg font-semibold">{p.h}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-border/60 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="text-3xl font-bold md:text-4xl">Stores I have worked on</h2>
              <Link to="/en/cases" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                All cases <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {cases.slice(0, 3).map((c) => {
                const en = enCases[c.slug];
                return (
                  <Link
                    key={c.slug}
                    to="/en/cases"
                    className="shadow-lift overflow-hidden rounded-lg border border-border bg-card"
                  >
                    <img
                      src={c.image}
                      alt={en.imageAlt}
                      width={c.width}
                      height={c.height}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[16/10] w-full border-b border-border bg-muted/20 object-contain object-top p-3"
                    />
                    <div className="p-5">
                      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                        {en.industry}
                      </p>
                      <h3 className="mt-2 text-lg font-semibold">{c.shop}</h3>
                      <p className="mt-2 text-sm text-muted-foreground">{en.claim}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-t border-border/60 py-16">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">How a sprint runs</h2>
            <ul className="mt-8 grid gap-3">
              {[
                "Diagnosis: analytics, recordings, checkout funnel, speed, where the money leaks out.",
                "Priorities: the handful of changes with the largest effect on revenue per session.",
                "Implementation: directly in your theme, staged and reviewed before it goes live.",
                "Verification: before/after on the same metrics, so the result is not a matter of taste.",
              ].map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-3 rounded-md border border-border bg-card px-4 py-3 text-sm"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <LeadSection source="en-home" lang="en" />
      </main>
      <SiteFooterEn />
    </div>
  );
}
